import { TownsquareTagAri } from '@atlassian/ari';
import { IdentityTeamAri } from '@atlassian/ari/identity';
import {
	getGoalSiteAri,
	getProjectSiteAri,
} from '@atlassian/projects-goals-common/get-app-site-ari';
import { PreloadedQuery } from 'react-relay';

import { formatIsoLocalDateTime } from '@townsquare/date-utils/format';
import {
	getUtcEndOfGoalMonth,
	getUtcEndOfWeekDate,
	parseGoalUpdatesFeedStartDate,
	parseProjectUpdatesFeedStartDate,
} from '@townsquare/date-utils/update';
import {
	convertQueryParamToUpdateStateFilter,
	getValidUpdateQueryParamFilter,
} from '@townsquare/home-view/query-param-filter';
import { createRelayResource } from '@townsquare/relay-utils';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { RouteResource } from '@townsquare/router/routing-types';
import type { ResourceContext } from '@townsquare/router/types';

import ReadingModeFullScreenQuery, {
	type ReadingModeFullScreenQuery as ReadingModeFullScreenQueryType,
} from '../ui/__generated__/ReadingModeFullScreenQuery.graphql';

const PLACEHOLDER_UUID = 'dac6d157-4ea3-4208-be35-d2a839d6051d';

const getTeamAri = (teamId: string): string => {
	// we wrap in a try/catch incase there are any issues parsing the id
	try {
		if (IdentityTeamAri.check(teamId)) {
			return teamId;
		} else {
			return IdentityTeamAri.create({ teamId }).toString();
		}
	} catch (error) {
		//TODO: send error metrics https://jbusiness.atlassian.net/browse/TWCJ-254
		return '';
	}
};

const getTagAri = (tagUuid: string, siteId: string): string => {
	try {
		return TownsquareTagAri.create({
			tagUuid,
			siteId,
		}).toString();
	} catch (error) {
		return '';
	}
};

export const readingModeResource: RouteResource<
	PreloadedQuery<ReadingModeFullScreenQueryType, Record<string, unknown>>
> = createRelayResource<ReadingModeFullScreenQueryType>({
	getQuery: (routerContext, resourceContext) => {
		const context = resourceContext as ResourceContext;
		const updateType = routerContext.match.params.updateType ?? '';
		const isProjectUpdates = updateType === 'projects';
		const isGoalUpdates = updateType === 'goals';
		const isTeamUpdates = updateType === 'team';
		const isTagUpdates = updateType === 'tag';
		const startQueryParam = routerContext.query.start;
		const teamId = getTeamAri(routerContext.query.teamId ?? '');
		const tagId = getTagAri(
			routerContext.query.id ?? PLACEHOLDER_UUID,
			context.workspace?.cloudId ?? '',
		);

		let startDate;
		let endDate;

		if (isProjectUpdates) {
			startDate = parseProjectUpdatesFeedStartDate(startQueryParam);
			endDate = getUtcEndOfWeekDate(startDate);
		} else {
			startDate = parseGoalUpdatesFeedStartDate(startQueryParam);
			endDate = getUtcEndOfGoalMonth(startDate);
		}

		const filter = routerContext.query.filter
			? convertQueryParamToUpdateStateFilter(
					getValidUpdateQueryParamFilter(routerContext.query.filter),
				)
			: undefined;

		const rawCloudId = context.workspace?.cloudId;
		if (!rawCloudId) throw new Error('No cloudId available');
		const containerId = isProjectUpdates
			? getProjectSiteAri(rawCloudId)
			: getGoalSiteAri(rawCloudId);

		return {
			query: ReadingModeFullScreenQuery,
			variables: {
				isProjectUpdates: isProjectUpdates,
				isGoalUpdates: isGoalUpdates,
				isTeamUpdates: isTeamUpdates,
				isTagUpdates: isTagUpdates,
				stateFilter: filter,
				createdAfter: formatIsoLocalDateTime(startDate),
				createdBefore: formatIsoLocalDateTime(endDate),
				containerId: containerId,
				teamId: teamId ?? 'fake-team-id',
				tagId: tagId ?? PLACEHOLDER_UUID,
			},
			environment: getAggRelayEnvironment(),
			options: {
				fetchPolicy: 'store-and-network',
			},
		};
	},
});
